import {
  Directive,
  ElementRef,
  HostListener,
  forwardRef,
  Renderer2,
  Input,
  Injector,
  OnInit,
} from "@angular/core";
import { DecimalPipe } from "@angular/common";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  NgControl,
  AbstractControl,
  Validator,
  ValidationErrors,
} from "@angular/forms";
import { APP_DEFAULTS } from "src/app/core/models/APP_DEFAULTS";

const CURRENCYDIRECTIVE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InputMaskDirective),
  multi: true,
};

const CURRENCYDIRECTIVE_VALIDAT0R: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => InputMaskDirective),
  multi: true,
};

interface ICursorPosition {
  start: number;
  end: number;
}
@Directive({
  selector:
    "input[formControlName][InputMask], input[formControl][InputMask], input[ngModel][InputMask]",
  providers: [
    DecimalPipe,
    CURRENCYDIRECTIVE_VALUE_ACCESSOR,
    CURRENCYDIRECTIVE_VALIDAT0R,
  ],
  standalone: true,
})
export class InputMaskDirective
  implements ControlValueAccessor, Validator, OnInit
{
  private defaults = APP_DEFAULTS;

  /**
   * @type {boolean}
   * @description Format value on blur event.
   * @default false
   */
  @Input() formatOnlyOnBlur = false;

  /**
   * @type {number}
   * @description Minimum value of the input field.
   * @default 0
   */
  @Input() min!: number;

  /**
   * @type {number}
   * @description Maximum value of the input field.
   * @default 99999999999999
   */
  @Input() max!: number;

  /**
   * @type {number}
   * @description Maximum number of digits after decimal point.
   * @default 2
   * @example for allowed values (1,000.00, 1,000.01, 1,000.10)
   */
  @Input() minFrac: number = +this.defaults.minFraction;

  /**
   * @type {number}
   * @description Maximum number of digits after decimal point.
   * @default 2
   * @example for allowed values (1,000.00, 1,000.01, 1,000.10)
   */
  @Input() maxFrac: number = +this.defaults.maxFraction;

  /**
   * @type {boolean}
   * @description Allow negative values in the input field.
   * @default false
   * @example for allowed values (-1,000.00, -1,000.01, -1,000.10, 1,000.00, 1,000.01, 1,000.10)
   */
  @Input() allowMinus: boolean = false;

  private decimalPointSeparator = this.getLocaleDecimalPointSeparator();
  private thousandsSeparator = this.getLocaleThousandsSeparator();

  private ngControl!: NgControl;
  private numbersAndDecimalPointSeparatorRegex: RegExp;
  private readonly allowedKeys: string[] = [
    "Backspace",
    "Tab",
    "End",
    "Home",
    "Delete",
    "Del",
    "Ctrl",
    "ArrowLeft",
    "ArrowRight",
    "Left",
    "Right",
  ];
  private readonly allowedKeysUsedWithControl: string[] = [
    "x",
    "c",
    "v",
    "a",
    "z",
  ];
  private numPattern: string = "[0-9]?";
  private lenOfSeperated: string = ``;
  private numberWithUpToDecimalsRegex = new RegExp(
    `^([0-9]+)?(\\${this.decimalPointSeparator}[0-9]?[0-9]?)?$`,
  );
  private previousRawInputValue = "";

  private onChange!: (value: string) => void;
  private onTouch!: () => void;

  constructor(
    private decimalPipe: DecimalPipe,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private injector: Injector,
  ) {
    const localeDecimalPointSeparator = this.getLocaleDecimalPointSeparator();
    const minus = this.allowMinus ? "-" : "";
    this.numbersAndDecimalPointSeparatorRegex = new RegExp(
      `[0-9${localeDecimalPointSeparator}${minus}]`,
      "g",
    );
  }

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl);
    if (this.minFrac > 0) {
      for (let i = 0; i < this.minFrac; i++)
        this.lenOfSeperated += this.numPattern;

      this.numberWithUpToDecimalsRegex = new RegExp(
        `^-?([0-9]+)?(\\${this.decimalPointSeparator}${this.lenOfSeperated})?$`,
      );
    } else {
      this.numberWithUpToDecimalsRegex = new RegExp(`^-?([0-9]+)?$`);
    }
  }

  @HostListener("blur")
  handleBlur() {
    this.reformatInput();
    this.onTouch();
  }

  @HostListener("input", ["$event.target.value", "$event.target", "$event"])
  handleInput(
    rawInputValue: string,
    inputEl: HTMLInputElement,
    event: InputEvent,
  ) {
    const enteredData = event.data;
    if (this.allowMinus && rawInputValue === "-") {
      this.previousRawInputValue = rawInputValue;
      this.setNativeInputValue(rawInputValue);
      this.onChange(""); // don't emit a number yet
      return;
    }
    if (this.max !== undefined && this.max !== null) {
      const value = this.parseLocaleStringToNumber(rawInputValue);
      if (!isNaN(value) && value > this.max) {
        const clamped = this.formatOnlyOnBlur
          ? String(this.max)
          : this.formatPriceTo2Decimal(this.max)!;
        this.previousRawInputValue = clamped;
        this.onChange(clamped);
        this.setNativeInputValue(clamped);
        this.setCursorPosition(inputEl, {
          start: clamped.length,
          end: clamped.length,
        });
        return;
      }
    }
    if (
      enteredData === this.decimalPointSeparator &&
      this.hasOnlyOneDecimalSeparatorAtTheEnd(rawInputValue)
    )
      return;

    if (rawInputValue === "") {
      this.onChange("");
      return;
    }

    const initialCursorPosition = this.getCursorPosition(inputEl);

    if (
      this.hasNoMoreThan2DecimalDigits(rawInputValue) &&
      !this.isNumberTooLarge(rawInputValue)
    ) {
      const newRawInputValue = this.formatOnlyOnBlur
        ? rawInputValue
        : this.formatPriceToUpTo2Decimal(rawInputValue);
      this.previousRawInputValue = newRawInputValue;
      this.onChange(newRawInputValue);
      this.setNativeInputValue(newRawInputValue);

      const diffInSeparators = this.getDiffInSeparators(
        rawInputValue,
        newRawInputValue,
      );
      this.moveCursor(inputEl, initialCursorPosition, diffInSeparators);
    } else if (
      enteredData?.length === 1 &&
      enteredData !== this.decimalPointSeparator &&
      initialCursorPosition.start !== rawInputValue?.length &&
      initialCursorPosition.end !== rawInputValue?.length &&
      !this.isNumberTooLarge(rawInputValue)
    ) {
      const newRawInputValue = this.replaceAt(
        this.previousRawInputValue,
        initialCursorPosition.start - 1,
        enteredData,
      );
      this.previousRawInputValue = newRawInputValue;
      this.onChange(newRawInputValue);
      this.setNativeInputValue(newRawInputValue);
      this.moveCursor(inputEl, initialCursorPosition);
    } else {
      this.setNativeInputValue(this.previousRawInputValue);
      const cursorDiff = enteredData ? -enteredData.length : 0;
      this.moveCursor(inputEl, initialCursorPosition, cursorDiff);
    }
  }

  @HostListener("keydown", ["$event"])
  keyDownEvent(event: KeyboardEvent) {
    if (this.isSpecialAllowedKey(event) || this.isNumberKey(event)) return;
    event.preventDefault();
  }

  private isNumberTooLarge(rawInputValue: string): boolean {
    const value = this.parseLocaleStringToNumber(rawInputValue);
    if (this.max !== undefined && this.max !== null && value > this.max)
      return true;
    return value >= 10000000000000;
  }

  private replaceAt(originalValue: string, index: number, replacement: string) {
    return (
      originalValue.substr(0, index) +
      replacement +
      originalValue.substr(index + replacement.length)
    );
  }

  private isNumberKey(event: KeyboardEvent): boolean {
    const key = event.key;
    if (key === "-" && this.allowMinus) {
      const input = this.getNativeInputValue();
      return (
        input.indexOf("-") === -1 &&
        this.getCursorPosition(this.elementRef.nativeElement).start === 0
      );
    }
    return !!String(key).match(this.numbersAndDecimalPointSeparatorRegex);
  }

  private isSpecialAllowedKey(event: KeyboardEvent): boolean {
    return (
      this.allowedKeys.includes(event.key) ||
      this.isAllowedKeyWithControl(event)
    );
  }

  private isAllowedKeyWithControl(event: KeyboardEvent): boolean {
    if (event.ctrlKey || event.metaKey)
      return this.allowedKeysUsedWithControl.includes(event.key.toLowerCase());

    return false;
  }

  private getValueWithoutThousandsSeparator(value: string): string {
    return value.split(this.thousandsSeparator).join("");
  }

  private hasNoMoreThan2DecimalDigits(value: string): boolean {
    const valueWithoutSeparators =
      this.getValueWithoutThousandsSeparator(value);
    return this.numberWithUpToDecimalsRegex.test(valueWithoutSeparators);
  }

  private reformatInput(value?: number): void {
    const newValue =
      value !== undefined
        ? value
        : this.parseLocaleStringToNumber(this.getNativeInputValue());
    if (newValue || newValue === 0) {
      const with2Decimals = this.formatPriceTo2Decimal(newValue);
      this.setNativeInputValue(with2Decimals!);
    } else {
      this.setNativeInputValue("");
    }
  }

  private formatPriceTo2Decimal(value: number | string): string | null {
    const numberToTransform =
      (typeof value === "string"
        ? this.parseLocaleStringToNumber(value)
        : value) || 0;
    return this.decimalPipe.transform(
      numberToTransform,
      `1.${this.minFrac}-${this.maxFrac}`,
    );
  }

  private formatPriceToUpTo2Decimal(value: string): string {
    // TODO: potential for refactor:
    const numberOfZerosAfterDecimalPointRegex = `^-?([0-9])*\\${this.decimalPointSeparator}(0+)$`;
    const numberOfZerosAfterFirstDecimalDigitRegex = `^-?([0-9])*\\${this.decimalPointSeparator}[1-9](0+)$`;
    const valueWithoutSeparators =
      this.getValueWithoutThousandsSeparator(value);
    const numberOfZerosAfterDecimalPoint =
      valueWithoutSeparators.match(numberOfZerosAfterDecimalPointRegex)?.[1]
        .length || 0;
    const numberOfZerosAfterFirstDecimalDigit =
      valueWithoutSeparators.match(
        numberOfZerosAfterFirstDecimalDigitRegex,
      )?.[1].length || 0;

    const formatted = this.decimalPipe.transform(
      this.parseLocaleStringToNumber(valueWithoutSeparators),
      "1.0-3",
    );
    if (numberOfZerosAfterDecimalPoint > 0) {
      let numOfZero = "";
      if (this.minFrac > 0)
        for (let i = 0; i < this.minFrac; i++) numOfZero += "0";
      return (
        formatted +
        this.decimalPointSeparator +
        `${numOfZero}`.slice(0, numberOfZerosAfterDecimalPoint)
      );
    }
    if (numberOfZerosAfterFirstDecimalDigit > 0) {
      return formatted + "0";
    }
    return formatted!;
  }

  private getNumberOfThousandsSeparators(value: string): number {
    return (value || "").split(this.thousandsSeparator).length - 1;
  }

  private getDiffInSeparators(oldValue: string, newValue: string): number {
    const initialNumberOfSeparators =
      this.getNumberOfThousandsSeparators(oldValue);
    const currentNumberOfSeparators =
      this.getNumberOfThousandsSeparators(newValue);
    return currentNumberOfSeparators - initialNumberOfSeparators;
  }

  private hasOnlyOneDecimalSeparatorAtTheEnd(value: string): boolean {
    return (
      value.split(this.decimalPointSeparator).length === 2 &&
      value.slice(-1) === this.decimalPointSeparator
    );
  }

  private parseLocaleStringToNumber(value: string): number {
    if (!value) return NaN;
    const cleanPattern = new RegExp(
      `[^0-9${this.decimalPointSeparator}-]`,
      "g",
    );
    const cleaned = value.replace(cleanPattern, "");
    const normalized = cleaned.replace(this.decimalPointSeparator, ".");
    return parseFloat(normalized);
  }

  private setNativeInputValue(value: string) {
    this.renderer.setProperty(this.elementRef.nativeElement, "value", value);
  }

  private getNativeInputValue(): string {
    return this.elementRef.nativeElement.value;
  }

  private getCursorPosition(inputEl: HTMLInputElement): ICursorPosition {
    return {
      start: inputEl.selectionStart ?? 0,
      end: inputEl.selectionEnd ?? 0,
    };
  }

  private setCursorPosition(
    inputEl: HTMLInputElement,
    position: ICursorPosition,
  ) {
    inputEl.setSelectionRange(position.start, position.end);
  }

  private moveCursor(
    inputEl: HTMLInputElement,
    initialPosition: ICursorPosition,
    diff: number = 0,
  ): void {
    this.setCursorPosition(inputEl, {
      start: initialPosition.start + diff,
      end: initialPosition.end + diff,
    });
  }

  private getLocaleDecimalPointSeparator() {
    const example = this.decimalPipe.transform(1.1, "1.1")!;
    return example.charAt(1);
  }

  private getLocaleThousandsSeparator() {
    const example = this.decimalPipe.transform(1000, "1.0")!;
    return example.length === 5 ? example.charAt(1) : "";
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = (value: string) => {
      const valueToEmit =
        value === "" ? null : this.parseLocaleStringToNumber(value);
      fn(valueToEmit!);
    };
  }

  registerOnTouched(callbackFunction: () => void): void {
    this.onTouch = callbackFunction;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(
      this.elementRef.nativeElement,
      "disabled",
      isDisabled,
    );
  }

  writeValue(value: number): void {
    if (typeof value === "string") return;
    this.reformatInput(+value?.toFixed(this.minFrac)!);
    this.previousRawInputValue = this.getNativeInputValue();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};
    if (control.value > this.max) errors["max"] = true;
    if (control.value < this.min) errors["min"] = true;
    return Object.keys(errors).length === 0 ? null : errors;
  }
}
